import { isObject } from 'lodash-es';
import type { Directive, DirectiveBinding } from 'vue';
import { nextFrame } from '@/components/p-tooltip/p-tooltip.helpers';
import type { Options, Placement, Value } from '@/components/p-tooltip/p-tooltip.types';
import { usePTooltip } from '@/components/p-tooltip/usePTooltip';

const { showTooltip, hideTooltip, isTooltipHovered } = usePTooltip();

// We use a WeakMap here to store each element's events and content
// to ensure that the tooltip data is garbage collected
// when the trigger element is no longer in use.
const tooltipData = new WeakMap<
  HTMLElement,
  {
    onMouseEnter: EventListener;
    onMouseLeave: EventListener;
    options: Options;
  }
>();

const createOptions = (binding: DirectiveBinding<Value, Placement>): Options => {
  const { value, modifiers } = binding;

  const options: Options = isObject(value) ? value : { content: null };

  options.content = typeof value === 'string' ? value : value?.content || null;
  options.placement = Object.keys(modifiers)[0] ? (Object.keys(modifiers)[0] as Placement) : options.placement || 'top';

  if (isObject(value) && value.stayOpenOnHover) {
    options.popperClass = 'p-tooltip-stay-open-on-hover';
    options.distance = 0;
  }

  return options;
};

const bind = (el: HTMLElement, binding: DirectiveBinding<Value, Placement>) => {
  const onMouseEnter = () => {
    const data = tooltipData.get(el);

    // If the content is null, we don't want to show the tooltip
    if (!data?.options || data?.options.content === null) return;

    showTooltip({ trigger: el, options: data.options });
  };

  const onMouseLeave = async () => {
    const data = tooltipData.get(el);

    if (data?.options.stayOpenOnHover === true) {
      await nextFrame();

      if (!isTooltipHovered.value) {
        hideTooltip();
      }
    } else {
      hideTooltip();
    }
  };

  // Initialize the tooltip data for this element
  tooltipData.set(el, {
    onMouseEnter,
    onMouseLeave,
    options: createOptions(binding),
  });

  el.addEventListener('mouseenter', onMouseEnter);
  el.addEventListener('mouseleave', onMouseLeave);
};

const destroy = (el: HTMLElement) => {
  const data = tooltipData.get(el);

  if (data) {
    el.removeEventListener('mouseenter', data.onMouseEnter);
    el.removeEventListener('mouseleave', data.onMouseLeave);
    tooltipData.delete(el);
  }
};

const vPTooltip: Directive<HTMLElement> = {
  mounted: bind,
  beforeUnmount(el: HTMLElement) {
    destroy(el);
  },
  updated: (el: HTMLElement, binding: DirectiveBinding<Value, Placement>) => {
    const data = tooltipData.get(el);
    const { value, oldValue } = binding;

    // Only update the content if the value has changed
    if (data && JSON.stringify(value) !== JSON.stringify(oldValue)) {
      data.options = createOptions(binding);
    }
  },
};

export { vPTooltip };
